import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, catchError, throwError } from 'rxjs';
import { environment } from '@brighthub/environment';
import type {
  StoryMetaResponse,
  StoryPointerResponse,
  StoryStepPayloadResponse,
  StoryStepKeyResponse,
  StoryStepCurrentResponse,
  StoryDemosResponse,
  StoryErrorBody,
} from './story-demo.types';

/**
 * Story Service API utility.
 *
 * Base URL and auth: Configured via environment (storyServiceBaseUrl, storyDemoKey, storyAdminKey).
 * Runtime routes use base path `/api` and header `x-demo-key`. Admin routes use `x-admin-key`.
 * Standard error shape: `{ error: { code, message, details? } }` with
 * codes UNAUTHORIZED | NOT_FOUND | BAD_REQUEST | INTERNAL | CONFLICT.
 *
 * Runtime: GET meta, pointer, current; POST pointer (setPointer), reset, jump,
 *   pointer/advance (→ StoryStepCurrentResponse), pointer/rewind (→ StoryStepCurrentResponse);
 *   GET steps/:stepNumber, steps/:stepNumber/keys/:endpointKey.
 * Admin: POST /api/admin/story/:demoId/versions, POST .../activate.
 *
 * advance/rewind return rich { pointer, version, payload } shape.
 * jump/setPointer/reset return flat { step, updatedAt } shape.
 */
@Injectable({ providedIn: 'root' })
export class StoryService {
  private readonly http = inject(HttpClient);

  private get baseUrl(): string {
    const configuredUrl = environment.storyServiceBaseUrl ?? '';
    const localOverride = environment.storyServiceLocalBaseUrl ?? '';
    const hostname = this.getCurrentHostname();
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
    const url = isLocalHost && localOverride ? localOverride : configuredUrl;
    return url.replace(/\/$/, '');
  }

  protected getCurrentHostname(): string | null {
    if (typeof window === 'undefined') return null;
    return window.location?.hostname ?? null;
  }

  private get headers(): Record<string, string> {
    const key = environment.storyDemoKey ?? '';
    return {
      'x-demo-key': key,
      'Content-Type': 'application/json',
    };
  }

  private get adminHeaders(): Record<string, string> {
    const key = environment.storyAdminKey ?? '';
    return {
      'x-admin-key': key,
      'Content-Type': 'application/json',
    };
  }

  private requestOptions(): { headers: Record<string, string> } {
    return { headers: { ...this.headers } };
  }

  private adminRequestOptions(): { headers: Record<string, string> } {
    return { headers: { ...this.adminHeaders } };
  }

  /** GET /api/story/demos — returns all demos with metadata and pointer state. */
  getDemos(): Promise<StoryDemosResponse> {
    return this.get<StoryDemosResponse>('/api/story/demos');
  }

  /** GET /api/story/:demoId/meta */
  getMeta(demoId: string): Promise<StoryMetaResponse> {
    return this.get<StoryMetaResponse>(`/api/story/${encodeURIComponent(demoId)}/meta`);
  }

  /** GET /api/story/:demoId/pointer */
  getPointer(demoId: string): Promise<StoryPointerResponse> {
    return this.get<StoryPointerResponse>(`/api/story/${encodeURIComponent(demoId)}/pointer`);
  }

  /** POST /api/story/:demoId/pointer body { step } */
  setPointer(demoId: string, step: number): Promise<StoryPointerResponse> {
    return this.post<StoryPointerResponse>(`/api/story/${encodeURIComponent(demoId)}/pointer`, { step });
  }

  /** POST /api/story/:demoId/reset */
  reset(demoId: string): Promise<StoryPointerResponse> {
    return this.post<StoryPointerResponse>(`/api/story/${encodeURIComponent(demoId)}/reset`, {});
  }

  /** POST /api/story/:demoId/jump body { step } */
  jump(demoId: string, step: number): Promise<StoryPointerResponse> {
    return this.post<StoryPointerResponse>(`/api/story/${encodeURIComponent(demoId)}/jump`, { step });
  }

  /** GET /api/story/:demoId/steps/:stepNumber */
  getStepPayload(demoId: string, stepNumber: number): Promise<StoryStepPayloadResponse> {
    return this.get<StoryStepPayloadResponse>(
      `/api/story/${encodeURIComponent(demoId)}/steps/${stepNumber}`
    );
  }

  /** GET /api/story/:demoId/steps/:stepNumber/keys/:endpointKey */
  getStepKey(
    demoId: string,
    stepNumber: number,
    endpointKey: string
  ): Promise<StoryStepKeyResponse> {
    return this.get<StoryStepKeyResponse>(
      `/api/story/${encodeURIComponent(demoId)}/steps/${stepNumber}/keys/${encodeURIComponent(endpointKey)}`
    );
  }

  /** GET /api/story/:demoId/current - returns pointer + full payload for current step. Does not mutate pointer. */
  getCurrent(demoId: string): Promise<StoryStepCurrentResponse> {
    return this.get<StoryStepCurrentResponse>(`/api/story/${encodeURIComponent(demoId)}/current`);
  }

  /** POST /api/story/:demoId/pointer/advance - atomically increments pointer; returns rich { pointer, version, payload }. Returns 409 CONFLICT at last step. */
  advancePointer(demoId: string): Promise<StoryStepCurrentResponse> {
    return this.post<StoryStepCurrentResponse>(
      `/api/story/${encodeURIComponent(demoId)}/pointer/advance`,
      {}
    );
  }

  /** POST /api/story/:demoId/pointer/rewind - atomically decrements pointer; returns rich { pointer, version, payload }. Returns 409 CONFLICT at step 1. */
  rewindPointer(demoId: string): Promise<StoryStepCurrentResponse> {
    return this.post<StoryStepCurrentResponse>(
      `/api/story/${encodeURIComponent(demoId)}/pointer/rewind`,
      {}
    );
  }

  /** POST /api/admin/story/:demoId/versions - requires storyAdminKey in environment. */
  postVersions(demoId: string, body: Record<string, unknown> = {}): Promise<Record<string, unknown>> {
    return this.postAdmin<Record<string, unknown>>(
      `/api/admin/story/${encodeURIComponent(demoId)}/versions`,
      body
    );
  }

  /** POST /api/admin/story/:demoId/activate - requires storyAdminKey in environment. */
  postActivate(demoId: string, body: Record<string, unknown> = {}): Promise<Record<string, unknown>> {
    return this.postAdmin<Record<string, unknown>>(
      `/api/admin/story/${encodeURIComponent(demoId)}/activate`,
      body
    );
  }

  isAvailable(): boolean {
    const url = this.baseUrl;
    const key = environment.storyDemoKey;
    return Boolean(url && key);
  }

  private get<T>(path: string): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    return firstValueFrom(
      this.http.get<T>(url, this.requestOptions()).pipe(catchError((e) => throwError(() => this.normalizeError(e))))
    );
  }

  private post<T>(path: string, body: object): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    return firstValueFrom(
      this.http.post<T>(url, body, this.requestOptions()).pipe(catchError((e) => throwError(() => this.normalizeError(e))))
    );
  }

  private postAdmin<T>(path: string, body: object): Promise<T> {
    const adminKey = environment.storyAdminKey ?? '';
    if (!adminKey) {
      return Promise.reject(
        new Error('Story Service admin routes are not configured (storyAdminKey missing).') as Error & {
          status?: number;
          code?: string;
        }
      );
    }
    const url = `${this.baseUrl}${path}`;
    return firstValueFrom(
      this.http
        .post<T>(url, body, this.adminRequestOptions())
        .pipe(catchError((e) => throwError(() => this.normalizeError(e))))
    );
  }

  private normalizeError(err: unknown): Error & { status?: number; code?: string } {
    if (err instanceof HttpErrorResponse) {
      const status = err.status ?? 0;
      const body = err.error as StoryErrorBody | undefined;
      const code = body?.error?.code;
      const isUnreachable = status === 0 || err.status == null;
      const message = isUnreachable
        ? 'StoryService unavailable. Unable to connect to backend.'
        : body?.error?.message ?? err.message ?? 'Story Service request failed';
      const e = new Error(message) as Error & { status?: number; code?: string };
      e.status = status;
      e.code = code;
      return e;
    }
    const isNetworkError = err instanceof Error && (
      err.message?.includes('Http failure') ||
      err.message?.includes('Network') ||
      err.message?.toLowerCase().includes('failed to fetch')
    );
    const message = isNetworkError
      ? 'StoryService unavailable. Unable to connect to backend.'
      : err instanceof Error ? err.message : String(err);
    const e = new Error(message) as Error & { status?: number; code?: string };
    e.status = 0;
    return e;
  }
}
